import { useEffect, useRef, type RefObject } from "react";

/**
 * Enter saves the New issue (the dialog's Create, the draft panel's close) —
 * but only when the person is not using the key for something else. These are
 * the rules, in one place, for every host of IssueDraftContent.
 */

/** Input types that are a control, not a place to type — Enter is theirs too. */
const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "hidden",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

/** Roles that take typed text. */
const TEXT_ENTRY_ROLES = new Set(["textbox", "searchbox", "combobox", "spinbutton"]);

/** Roles Enter activates natively (or by the widget's own handler). */
const ACTIVATING_ROLES = new Set([
  "button",
  "link",
  "checkbox",
  "radio",
  "switch",
  "tab",
  "menuitem",
  "menuitemcheckbox",
  "menuitemradio",
  "option",
  "treeitem",
  "gridcell",
  "slider",
]);

/**
 * Containers that own the keyboard while focus is anywhere inside them: an
 * open list or menu, a calendar grid, a cmdk list, rich text.
 */
const KEYBOARD_OWNER_SELECTOR = [
  '[role="listbox"]',
  '[role="menu"]',
  '[role="menubar"]',
  '[role="grid"]',
  '[role="tree"]',
  '[role="combobox"]',
  "[cmdk-root]",
  '[contenteditable]:not([contenteditable="false"])',
].join(",");

/**
 * Radix keeps a closing layer mounted for its exit animation, marked
 * `data-state="closed"`; it no longer has the keyboard.
 */
const OPEN = ':not([data-state="closed"])';

/**
 * Layers that, while open anywhere on the page, mean Enter is not for us: a
 * Radix Select's list, a dropdown menu, and a question being asked (the
 * "needs a description" warning). They never wrap the New issue itself.
 *
 * A composer's suggestion list (`data-composer-list`, the @-mention list) is
 * not one: it is open only while its own text field has focus, and that field
 * keeps Enter itself — open in another composer, it is no reason to refuse.
 */
const BLOCKING_LAYER_SELECTOR = [
  '[role="listbox"]:not([data-composer-list])',
  '[role="menu"]',
  '[role="alertdialog"]',
]
  .map((s) => `${s}${OPEN}`)
  .join(",");

/** Any open layer — a popover, a date picker, another dialog — not hosting `scope`. */
const ANY_LAYER_SELECTOR = `${BLOCKING_LAYER_SELECTOR},[role="dialog"]${OPEN}`;

/**
 * A layer opened from inside the New issue and drawn inside it. The host
 * itself (a dialog too) never counts.
 */
const NESTED_LAYER_SELECTOR = `[role="dialog"]${OPEN},[role="alertdialog"]${OPEN}`;

/** The wrapper Radix puts around a popover's content; focus can rest on it. */
const POPPER_WRAPPER_SELECTOR = "[data-radix-popper-content-wrapper]";

/**
 * An open popover (the Tags picker, a date picker). Popovers portal to the
 * body, so this is found on the document, not inside the New issue. Matched
 * through the popper wrapper so a dialog the New issue sits on top of does
 * not count.
 */
const POPOVER_LAYER_SELECTOR = `${POPPER_WRAPPER_SELECTOR} > [role="dialog"][data-state="open"]`;

/**
 * Is a popover opened from inside the New issue open? Its trigger (in
 * `scope`) names it with `aria-controls`; a popover open elsewhere on the
 * page — /issues has plenty — is not ours to wait for.
 */
function hasOwnOpenPopover(doc: Document, scope: Element): boolean {
  return Array.from(doc.querySelectorAll(POPOVER_LAYER_SELECTOR)).some(
    (layer) =>
      !scope.contains(layer) &&
      layer.id !== "" &&
      scope.querySelector(`[aria-controls="${CSS.escape(layer.id)}"]`) != null
  );
}

/**
 * Is this keydown the Enter that confirms an IME candidate (Japanese, Chinese,
 * Korean)? Safari fires it with `isComposing` false, so keyCode 229 is
 * checked too. Every Enter handler in the New issue skips these.
 */
export function isImeEnter(event: { isComposing?: boolean; keyCode?: number }): boolean {
  return event.isComposing === true || event.keyCode === 229;
}

/** Is this element somewhere the person types (where Enter is a newline or a submit)? */
export function isTextEntryTarget(el: Element): boolean {
  if (el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement) return true;
  if (el instanceof HTMLInputElement) return !NON_TEXT_INPUT_TYPES.has(el.type);
  const role = el.getAttribute("role");
  if (role != null && TEXT_ENTRY_ROLES.has(role)) return true;
  // jsdom implements no isContentEditable; the attribute is what it reflects.
  return el.closest('[contenteditable]:not([contenteditable="false"])') != null;
}

/** Does Enter on this element already do something — press it, pick it, open it? */
export function isActivatingTarget(el: Element): boolean {
  if (el instanceof HTMLButtonElement || el instanceof HTMLInputElement) return true;
  if (el instanceof HTMLAnchorElement && el.hasAttribute("href")) return true;
  if (el.tagName === "SUMMARY") return true;
  if (el.getAttribute("aria-expanded") === "true") return true;
  const role = el.getAttribute("role");
  return role != null && ACTIVATING_ROLES.has(role);
}

/** The parts of a keydown the decision reads — a DOM KeyboardEvent satisfies it. */
export interface EnterKeyEventLike {
  key: string;
  keyCode?: number;
  isComposing?: boolean;
  repeat?: boolean;
  shiftKey: boolean;
  ctrlKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  defaultPrevented: boolean;
  target: EventTarget | null;
}

function hasNestedLayer(scope: Element): boolean {
  return Array.from(scope.querySelectorAll(NESTED_LAYER_SELECTOR)).some((layer) => layer !== scope);
}

function hasOpenLayer(doc: Document, selector: string, scope: Element): boolean {
  return Array.from(doc.querySelectorAll(selector)).some(
    (layer) => !scope.contains(layer) && !layer.contains(scope)
  );
}

/**
 * Should this keydown save the New issue drawn in `scope`?
 *
 * Only a plain, first Enter (no modifier, no auto-repeat, no IME composition,
 * nobody handled it already), aimed at `scope` itself or at a non-interactive
 * part of it — or at the page body, as after the panel's description is left
 * with Enter, when no other layer is open. Everything that uses Enter itself
 * keeps it: text fields, buttons and links, pickers and their lists, menus,
 * date grids, a popover open inside the New issue, and a question being asked.
 *
 * The target is read from the DOM, not the React tree: portalled content
 * (a Select's list, a popover) is outside `scope` even though React bubbles
 * its events through it.
 */
export function shouldEnterSave(event: EnterKeyEventLike, scope: Element): boolean {
  if (event.key !== "Enter") return false;
  if (event.defaultPrevented || event.repeat) return false;
  if (isImeEnter(event)) return false;
  if (event.shiftKey || event.ctrlKey || event.altKey || event.metaKey) return false;

  const target = event.target;
  if (!(target instanceof Element)) return false;
  const doc = target.ownerDocument;
  if (hasOpenLayer(doc, BLOCKING_LAYER_SELECTOR, scope)) return false;
  // A popover or question open (focus on it, or not).
  if (hasNestedLayer(scope) || hasOwnOpenPopover(doc, scope)) return false;

  if (target === doc.body || target === doc.documentElement) {
    // Nothing has focus. Only ours when no popover or other dialog is open.
    return !hasOpenLayer(doc, ANY_LAYER_SELECTOR, scope);
  }
  if (!scope.contains(target)) return false;
  if (isTextEntryTarget(target) || isActivatingTarget(target)) return false;
  if (target.closest(POPPER_WRAPPER_SELECTOR) != null) return false;
  const owner = target.closest(KEYBOARD_OWNER_SELECTOR);
  return owner == null || !scope.contains(owner);
}

/**
 * Listen for Enter on the document while `enabled`, and call `onSave` when
 * {@link shouldEnterSave} says the key is ours. `scopeRef` is the New issue's
 * own element; its host (`[data-issue-panel]`: the dialog content or the
 * draft panel) is the scope, so focus parked on the host itself counts.
 *
 * On the document rather than a React handler because after the panel's
 * description is left, focus is on the body — outside every React element.
 */
export function useEnterToSave(
  scopeRef: RefObject<HTMLElement>,
  onSave: (() => void) | undefined,
  enabled: boolean
): void {
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const active = enabled && onSave != null;

  useEffect(() => {
    if (!active) return;
    const handler = (event: KeyboardEvent) => {
      const el = scopeRef.current;
      const save = onSaveRef.current;
      if (!el || !save) return;
      const scope = el.closest("[data-issue-panel]") ?? el;
      if (!shouldEnterSave(event, scope)) return;
      event.preventDefault();
      save();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [active, scopeRef]);
}
